import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import classNames from 'classnames';
import { balloonEditorCommentConfig } from '@pwc/pwc-deals-ui/build/ckeditor/CKEditorConfig';
import {
  WordCountComponent,
  RichTextBalloonInlineEditor,
  CommentsAdapter,
} from '@pwc/pwc-deals-ui';
import { isInlineCommentingEnabled } from 'common/helpers/featureFlags';
import useCommentsAdapter from '../RichTextEditor/useCummentsAdapter';
import CalloutBoxContainer from '../CalloutBox/CalloutBoxContainer';
import './RichTextBalloonEditorFormikField.scss';
import { useIsAddingCommentEnabled } from '../../hooks/useIsAddingCommentEnabled';

const baseClass = 'rte-formik-balloon-field';
export const defaultDataTestId = 'rte-formik-field-container';

const RichTextBalloonEditorFormikField = ({
  field: { value, name },
  form: { setFieldValue },
  label,
  dataTestId = defaultDataTestId,
  config,
  customPlaceholder,
  suggestedLength,
  hideWordCountWhenInactive,
  withContainer,
  containerLabel,
  containerIcon,
  commentTarget,
  showCommentIcon = false,
}) => {
  const [readyToEdit, setReadyToEdit] = useState(false);
  const editorRef = useRef(null);
  const wordCountRef = useRef(null);
  const [charCount, setCharCount] = useState(0);
  const [isInitialized, setIsInitialized] = useState(false);
  const { currentUser } = useCommentsAdapter();

  const isAddingCommentEnabled = useIsAddingCommentEnabled();

  const prepareAndGetConfig = useCallback(() => {
    const configLocal =
      isAddingCommentEnabled && showCommentIcon
        ? balloonEditorCommentConfig
        : config;

    if (customPlaceholder) {
      configLocal.placeholder = customPlaceholder;
    }

    if (isAddingCommentEnabled && showCommentIcon) {
      return {
        ...configLocal,
        extraPlugins: [...configLocal.extraPlugins, CommentsAdapter],
      };
    }

    return configLocal;
  }, [config, customPlaceholder, isAddingCommentEnabled, showCommentIcon]);

  const getContainerClasses = useCallback(() => {
    return classNames(
      baseClass,
      !value && `${baseClass}--no-value`,
      readyToEdit && `${baseClass}--ready-to-edit`
    );
  }, [readyToEdit, value]);

  const showWordcount = useMemo(
    () =>
      (hideWordCountWhenInactive && readyToEdit) || !hideWordCountWhenInactive,
    [hideWordCountWhenInactive, readyToEdit]
  );

  const handleLabelClick = () => {
    setReadyToEdit(true);
  };

  const handleChange = (newValue) => {
    setFieldValue(name, newValue);
  };

  const handleFocus = () => {
    setReadyToEdit(true);
  };

  const handleBlur = () => {
    setReadyToEdit(false);
  };

  useEffect(() => {
    const evtHandler = (evt, stats) => {
      setCharCount(stats.characters);
    };

    const refInstance = wordCountRef.current;

    if (refInstance) {
      setCharCount(refInstance.characters);

      refInstance.on('update', evtHandler);
    }

    return () => {
      (refInstance || { off: () => null }).off('update', evtHandler);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInitialized, wordCountRef.current]);

  useEffect(() => {
    if (readyToEdit && editorRef.current) {
      editorRef.current.editing.view.focus();
    }
  }, [readyToEdit]);

  return (
    <div data-testid={dataTestId} className={getContainerClasses()}>
      <CalloutBoxContainer
        iconName={containerIcon}
        headerLabel={containerLabel}
        ignore={!withContainer}
        onClick={handleLabelClick}
      >
        <RichTextBalloonInlineEditor
          fieldKey={commentTarget}
          label={label}
          data={value}
          config={prepareAndGetConfig()}
          editorReference={editorRef}
          wordCountReference={wordCountRef}
          handleChange={handleChange}
          handleLabelClick={handleLabelClick}
          handleFocus={handleFocus}
          handleBlur={handleBlur}
          setIsInitialized={setIsInitialized}
          currentUser={currentUser}
          init={(editor) => {
            editorRef.current = editor;

            editor.keystrokes.set('Ctrl+Shift+space', (_, stop) => {
              editor.execute('input', { text: '\u00a0' });
              stop();
            });
          }}
          onAddCommentThread={() => {}}
          isInlineCommentingEnabled={isInlineCommentingEnabled}
        />
      </CalloutBoxContainer>
      {showWordcount && (
        <WordCountComponent
          charCount={charCount}
          allowedCount={suggestedLength}
        />
      )}
    </div>
  );
};

export default RichTextBalloonEditorFormikField;
